import React, { useState, useRef, useCallback } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import SendIcon from '@mui/icons-material/Send';
import SpeechToText from '../SpeechToText';

const MAX_CHARS = 4000;
const WARN_THRESHOLD = 0.8;

const TrainingChatInput = ({ onSend, disabled, sendError, onClearError }) => {
  const [inputText, setInputText] = useState('');
  const inputRef = useRef(null);
  const charCount = inputText.length;
  const showCounter = charCount >= MAX_CHARS * WARN_THRESHOLD;
  const atLimit = charCount >= MAX_CHARS;

  // Handle speech transcript — append to current input
  const handleSpeechTranscript = useCallback((transcript) => {
    if (transcript.trim()) {
      setInputText((prevText) => {
        const newText = prevText
          ? `${prevText} ${transcript.trim()}`
          : transcript.trim();
        return newText;
      });

      // Focus and position cursor at end
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          const length = inputRef.current.value.length;
          inputRef.current.setSelectionRange(length, length);
        }
      }, 100);
    }
  }, []);

  const handleSend = async () => {
    const text = inputText.trim();
    if (!text || disabled) return;
    setInputText('');
    await onSend(text);
    if (inputRef.current) inputRef.current.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper', flexShrink: 0 }}>
      {sendError && (
        <Alert severity="error" onClose={onClearError} sx={{ mb: 1 }}>
          {sendError}
        </Alert>
      )}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
        <TextField
          fullWidth
          multiline
          maxRows={4}
          placeholder="Type your message to the patient..."
          value={inputText}
          onChange={(e) => {
            if (e.target.value.length <= MAX_CHARS) {
              setInputText(e.target.value);
            }
          }}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          inputRef={inputRef}
          inputProps={{ maxLength: MAX_CHARS }}
          sx={{
            '& .MuiOutlinedInput-root': { borderRadius: 3 },
          }}
        />
        <SpeechToText
          onTranscript={handleSpeechTranscript}
          disabled={disabled}
          language="en-US"
          continuous={false}
          interimResults={true}
          showInterimText={false}
        />
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={disabled || !inputText.trim()}
          sx={{ minWidth: 48, borderRadius: 3 }}
        >
          <SendIcon />
        </Button>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          Press Enter to send, Shift+Enter for new line
        </Typography>
        {showCounter && (
          <Typography
            variant="caption"
            color={atLimit ? 'error' : 'text.secondary'}
            sx={{ fontWeight: atLimit ? 600 : 400 }}
          >
            {charCount.toLocaleString()} / {MAX_CHARS.toLocaleString()}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default TrainingChatInput;
